import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from '@/lib/auth/use-auth'
import type { Capability } from '@/lib/auth'

/**
 * UI-level gating only. NOT a security boundary — every capability must be
 * re-checked on the server.
 */
export function ProtectedRoute({
  children,
  capability,
}: {
  children: ReactNode
  capability?: Capability
}) {
  const { status, session, can } = useAuth()
  const location = useLocation()

  if (status === 'loading') return <FullPageSpinner />

  if (status === 'anonymous' || !session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }

  // Admin-issued accounts must set their own password before going further.
  if (session.user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }

  if (capability && !can(capability)) {
    return <Navigate to="/forbidden" replace />
  }

  return <>{children}</>
}

function FullPageSpinner() {
  return (
    <div className="grid min-h-full place-items-center">
      <svg
        className="animate-spin text-primary"
        width="28"
        height="28"
        viewBox="0 0 16 16"
        fill="none"
        role="status"
        aria-label="Loading"
      >
        <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path
          d="M14.5 8A6.5 6.5 0 0 0 8 1.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </div>
  )
}
